import type { EnvironmentProviders } from '@angular/core';
import { inject, makeEnvironmentProviders, provideAppInitializer } from '@angular/core';
import {
    CUSTOM_SPAWN_BRACKET_PERSISTENCE,
    CUSTOM_SPAWN_SIMULATOR_SESSION_PERSISTENCE,
} from '@animus/utility/web/custom-spawn-persistence';
import { HORDE_SESSION_PERSISTENCE, WEBRTC_SIGNALING_URL } from '@animus/utility/web/persistence';
import { RxDBCustomSpawnBracketPersistence } from './rxdb-custom-spawn-bracket-persistence';
import { RxDBCustomSpawnSimulatorSessionPersistence } from './rxdb-custom-spawn-simulator-session-persistence';
import { RxDBHordeSessionPersistence } from './rxdb-horde-session-persistence';
import { RxDatabaseService } from './rxdb.service';

export interface AnimusRxDBConfig {
    /** WebRTC signaling server URL used by Horde and bracket replication pools. */
    signalingUrl: string;
}

/**
 * Configures the application's RxDB persistence stack through Angular DI.
 *
 * Returns an `EnvironmentProviders` array that registers every provider the persistence library owns, so the
 * application config does not repeat them:
 *
 * 1. An app initializer that eagerly creates the database. App initializers may be asynchronous (they gate
 *    bootstrap), which is why this is an app initializer and not an environment initializer -
 *    `ENVIRONMENT_INITIALIZER` callbacks must be synchronous. Angular awaits the returned promise before
 *    bootstrap completes, so `RxDatabaseService.database` is guaranteed set once the app renders.
 * 2. The `HORDE_SESSION_PERSISTENCE` and `CUSTOM_SPAWN_BRACKET_PERSISTENCE` concrete implementations.
 * 3. The `WEBRTC_SIGNALING_URL` value from the supplied config.
 * 4. The dexie (IndexedDB) storage backend. Tests override `ANIMUS_RXDB_STORAGE` with in-memory storage.
 *
 * The dynamic `import()`s inside {@link RxDatabaseService.initialize} and the replication stores keep rxdb +
 * dexie + the reactivity/WebRTC plugins in lazy chunks, so the initial bundle does not grow - the eager
 * initializer just forces the database chunk to load at startup.
 *
 * @example
 * // app.config.ts
 * export const appConfig: ApplicationConfig = {
 *     providers: [
 *         provideAnimusRxDB({ signalingUrl: environment.webrtcSignalingUrl }),
 *         // ...
 *     ],
 * };
 */
export function provideAnimusRxDB({ signalingUrl }: AnimusRxDBConfig): EnvironmentProviders {
    return makeEnvironmentProviders([
        provideAppInitializer(() => inject(RxDatabaseService).initialize()),
        { provide: CUSTOM_SPAWN_BRACKET_PERSISTENCE, useClass: RxDBCustomSpawnBracketPersistence },
        {
            provide: CUSTOM_SPAWN_SIMULATOR_SESSION_PERSISTENCE,
            useClass: RxDBCustomSpawnSimulatorSessionPersistence,
        },
        { provide: HORDE_SESSION_PERSISTENCE, useClass: RxDBHordeSessionPersistence },
        { provide: WEBRTC_SIGNALING_URL, useValue: signalingUrl },
    ]);
}
